import { Location } from '@angular/common';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { HttpStatusEnum } from '@core/api';
import { getGroupIdFromUrl } from '@core/helpers';
import { CurrentUserService } from '@core/services';
import { UserGroupService } from '@core/services/user-group.service';
import { UserRoleEnum } from '@shared/enums';
import { AppRoutes } from '@shared/helpers';

export function userAndGroupGuard(configs = { canAccessWithNoGroups: false }): CanActivateFn {
    return async (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const currentUserService = inject(CurrentUserService);
        const router = inject(Router);
        const userGroupService = inject(UserGroupService);
        const location = inject(Location);
        const initialGroupId = getGroupIdFromUrl(state.url);

        if (!location.path(false) && location.path(true)) {
            const params = getHashParams();
            if (params.get('access_token') && params.get('expires_in') && params.get('token_type')) {
                window.location.href = `${window.location.protocol}//${window.location.host}`;
            }
        }

        //! This is a better piece of code, but the backend runs into issues when registering with a new account
        //! There seems to be a sync issue with the user data in OM
        // const [canAccessBasedOnUser, canAccessBasedOnGroups] = await Promise.all([
        //     handleCurrentUser(route, currentUserService, router),
        //     handleGroups(configs.canAccessWithNoGroups, userGroupService, initialGroupId, state.url),
        // ]);
        // if (canAccessBasedOnUser !== true) return canAccessBasedOnUser;
        // if (canAccessBasedOnGroups !== true) return canAccessBasedOnGroups;

        const canAccessBasedOnGroups = await handleGroups(
            configs.canAccessWithNoGroups,
            userGroupService,
            initialGroupId,
            state.url
        );
        if (canAccessBasedOnGroups !== true) return canAccessBasedOnGroups;

        const canAccessBasedOnUser = await handleCurrentUser(route, currentUserService, router);
        if (canAccessBasedOnUser !== true) return canAccessBasedOnUser;

        if (!userGroupService.currentGroup$.value) {
            const newUrlTree = await userGroupService.getRedirectUrlTree(initialGroupId, state.url);
            if (newUrlTree) return newUrlTree;
        }

        return true;
    };
}

function getHashParams() {
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    return params;
}

async function handleCurrentUser(
    route: ActivatedRouteSnapshot,
    currentUserService: CurrentUserService,
    router: Router
): Promise<boolean> {
    const currentUser = await currentUserService.initializeCurrentUser();

    if (currentUser) {
        return checkAccess(currentUserService.getCurrentUserRole(), route.data['roles'] || [], router);
    }
    currentUserService.triggerUserDetailsError(true);
    return false;
}

function checkAccess(role: UserRoleEnum, access: UserRoleEnum[], router: Router): boolean {
    if (access.length === 0 || access.some(r => r === role)) {
        return true;
    }
    router.navigate([AppRoutes.routes().DASHBOARD]);
    return false;
}

async function handleGroups(
    canAccessWithNoGroups: boolean,
    userGroupService: UserGroupService,
    groupId: number,
    currentUrl: string
): Promise<boolean | UrlTree> {
    if (!userGroupService.groups$.value) {
        const response = await userGroupService.initializeUserGroups();
        if (response.status !== HttpStatusEnum.SUCCESS && !canAccessWithNoGroups) return false;
    }

    if (!userGroupService.groups$.value?.length && !canAccessWithNoGroups) {
        return userGroupService.getNavigateToGroupListUrlTree(groupId, currentUrl);
    }

    return true;
}
